import { Divider, EDividerVariant, GeneralSearchIcon, Text } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useRouter } from '@/hooks/useRouter';
import { ESearchDropdownAlignment } from '@/hooks/useSearchDropdownAlignment';
import { EVehicleType, setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import {
  getHeaderSearchAddressFilter,
  getHeaderSearchDatesFilter,
  getHeaderSearchDriverAgeFilter,
  getHeaderSearchGuestsFilter,
  getHeaderSearchVehicleTypeFilter,
} from '@/redux/selectors/globalHeader';
import { trackSearchFilterToggled } from '@/services/analytics/search';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { formatDateRangeWithFlexibleDays } from '@/utility/format-date';
import { yieldToMain } from '@/utility/yieldToMain';

import { useApplySearch } from './applySearch';
import { DateFilterButton } from './DateFilterButton';
import { DateFilterContent } from './DateFilterContent';
import { DriverAgeFilterButton } from './DriverAgeFilterButton';
import { DriverAgeFilterContent } from './DriverAgeFilterContent';
import { GuestsFilterButton } from './GuestsFilterButton';
import { GuestsFilterContent } from './GuestsFilterContent';
import { LocationAutocompleteProvider } from './LocationAutocompleteContext';
import { LocationAutocompleteMenu } from './LocationAutocompleteMenu';
import { LocationFilterContent } from './LocationFilterContent';
import { LocationFilterProvider } from './LocationFilterContext';
import { LocationFilterInput } from './LocationFilterInput';
import { TripInformationHomeContent } from './TripInformationHomeContent';
import { VehicleTypeFilterButton } from './VehicleTypeFilterButton';
import { VehicleTypeFilterContent } from './VehicleTypeFilterContent';

export enum EHomeSearchWidgetVariant {
  RvRentals,
  RvDelivery,
  AutoRentals,
  Escapes,
}

type THomeSearchWidget = {
  variant: EHomeSearchWidgetVariant;
  desktopClassName?: string;
  mobileClassName?: string;
  className?: string;
};

export const HomeSearchWidget = ({
  variant = EHomeSearchWidgetVariant.RvRentals,
  desktopClassName = '',
  mobileClassName = '',
  className = '',
}: THomeSearchWidget) => {
  const intl = useIntl();
  const router = useRouter();
  const dispatch = useDispatch();

  const { isAboveDesktop } = useBreakpoint();

  const addressSelection = useAddressSelection();
  const datesSelection = useDatesSelection();
  const guestsSelection = useGuestsSelection();
  const driverAgeSelection = useDriverAgeSelection();
  const totalGuests = useTotalGuestsFromSelection();
  const vehicleTypeSelection = useVehicleTypeSelection();

  const containerRef = useRef<HTMLDivElement>(null);
  const locationFilterButtonRef = useRef<HTMLDivElement>(null);
  const dateFilterButtonRef = useRef<HTMLDivElement>(null);
  const guestsFilterButtonRef = useRef<HTMLDivElement>(null);
  const driverAgeFilterButtonRef = useRef<HTMLDivElement>(null);
  const vehicleTypeFilterButtonRef = useRef<HTMLDivElement>(null);

  const [openedFilter, setOpenedFilter] = useState<ESearchWidgetFilter>();

  const userInteractedWithLocationRef = useRef(false);
  const userInteractedWithDriverAgeRef = useRef(false);

  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const shouldDisplayDriverAgeFilter =
    variant === EHomeSearchWidgetVariant.RvRentals ||
    variant === EHomeSearchWidgetVariant.RvDelivery;

  useEffect(() => {
    if (!totalGuests) {
      dispatch(setHeaderSearchFilter({ guests: { adults: 2 } }));
    }
  }, [totalGuests, dispatch]);

  useEffect(() => {
    if (
      shouldDisplayDriverAgeFilter &&
      !driverAgeSelection &&
      !userInteractedWithDriverAgeRef.current
    ) {
      dispatch(setHeaderSearchFilter({ driverAge: 25 }));
    }
  }, [shouldDisplayDriverAgeFilter, driverAgeSelection, dispatch, userInteractedWithDriverAgeRef]);

  const setOpenedFilterYTM = (filter?: ESearchWidgetFilter) => {
    yieldToMain(() => setOpenedFilter(filter));

    if (!userInteractedWithLocationRef.current && filter === ESearchWidgetFilter.Location) {
      userInteractedWithLocationRef.current = true;
    }

    if (!userInteractedWithDriverAgeRef.current && filter === ESearchWidgetFilter.DriverAge) {
      userInteractedWithDriverAgeRef.current = true;
    }
  };

  const toggleTargetFilter = useCallback(
    (targetFilter: ESearchWidgetFilter) => {
      return () => setOpenedFilterYTM(openedFilter === targetFilter ? undefined : targetFilter);
    },
    [openedFilter],
  );

  const [, { update: updatePrevBaseUrl }] = useLocalStorage('prevBaseUrl');
  const applySearch = useApplySearch(variant);

  const handleSearch = useCallback(async () => {
    setOpenedFilter(undefined);
    updatePrevBaseUrl(router.route);
    await applySearch();
  }, [applySearch, router.route, updatePrevBaseUrl]);

  const getSearchBarLabel = () => {
    if (variant === EHomeSearchWidgetVariant.RvDelivery) {
      return intl.formatMessage({
        defaultMessage: 'Search stationary delivery locations',
        id: 'y2blIX',
        description: 'Home Search Widget > Delivery Location Filter Placeholder',
      });
    }
    if (variant === EHomeSearchWidgetVariant.RvRentals && isRentalsVDeliverySearchModeEnabled) {
      return intl.formatMessage({
        defaultMessage: 'Pickup location',
        id: 'Yxgq0K',
        description: 'Home Search Widget > Location Filter Placeholder',
      });
    }

    return intl.formatMessage({
      defaultMessage: 'Location',
      id: '7TGFRI',
      description: 'Home Search Widget > Escapes Location Filter Placeholder',
    });
  };

  const datesButtonLabel = datesSelection
    ? datesSelection
    : intl.formatMessage({
        defaultMessage: 'Add dates',
        id: 'zP42BU',
        description: 'Global Header > Placeholder text for dates button on mobile bar',
      });

  const guestsButtonLabel = guestsSelection
    ? guestsSelection
    : intl.formatMessage({
        defaultMessage: 'Add guests',
        id: 'k/FNcF',
        description: 'Global Header > Placeholder text for guests button on mobile bar',
      });

  const driverAgeButtonLabel = driverAgeSelection
    ? driverAgeSelection
    : intl.formatMessage({
        defaultMessage: 'Add driver age',
        id: '70dPzC',
        description: 'Global Header > Placeholder text for driver age button on mobile bar',
      });

  const vehicleTypeOptions: Record<EVehicleType, string> = {
    [EVehicleType.ALL]: intl.formatMessage({
      defaultMessage: 'Show me all listings',
      id: '7RXrSZ',
      description: 'Home Search Widget > Vehicle Type Filter All Option',
    }),
    [EVehicleType.DRIVABLE]: intl.formatMessage({
      defaultMessage: 'Drivable',
      id: 'RzX/xV',
      description: 'Home Search Widget > Vehicle Type Filter Drivable Option',
    }),
    [EVehicleType.TOWABLE]: intl.formatMessage({
      defaultMessage: 'Towable',
      id: 'm/6iOk',
      description: 'Home Search Widget > Vehicle Type Filter Towable Option',
    }),
    [EVehicleType.DELIVERABLES]: intl.formatMessage({
      defaultMessage: 'Delivery',
      id: 'D10Fj/',
      description: 'Home Search Widget > Vehicle Type Filter Deliverables Option',
    }),
  };

  const vehicleTypeButtonLabel =
    vehicleTypeSelection && vehicleTypeOptions[vehicleTypeSelection]
      ? vehicleTypeOptions[vehicleTypeSelection]
      : intl.formatMessage({
          defaultMessage: 'Add vehicle type',
          id: '5ymcao',
          description: 'Global Header > Placeholder text for vehicle type button on mobile bar',
        });

  const trackFilterToggled = (
    oldSelection: Record<string, unknown>,
    selection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => {
    trackSearchFilterToggled({
      newSelectedFilters: selection,
      previousSelectedFilters: oldSelection,
      modifiedWidgetFilter,
      source: 'home_search',
    });
  };

  useEffect(() => {
    // We are using dialogs for mobile, so we don't need to handle clicks outside the container
    if (!isAboveDesktop) return;

    const handlePressEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Prevent closing the entire menu, only close the dropdown if it is open
        const overlayExists =
          document.querySelector('.home-search-widget-dropdown-overlay') !== null;

        if (!overlayExists) setOpenedFilterYTM(undefined);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      // The dropdown element is outside the `#app` container.
      // To ensure we don't mistakenly close the filters modal, we check if the click event happens inside `#app`.
      // This prevents unintended closures when interacting with elements outside `#app`.
      const appContainer = document.querySelector('#app');

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        // To avoid close search when click on search input that is outside the filter container
        appContainer?.contains(event.target as Node)
      ) {
        setOpenedFilterYTM(undefined);
      }
    };

    document.addEventListener('keydown', handlePressEscape, true);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('keydown', handlePressEscape, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isAboveDesktop]);

  const searchButton = (
    <div
      className="flex items-center pr-1.5 gap-4 rounded-r-[inherit]"
      data-testid="desktop-search-button">
      <button
        className="flex items-center justify-center h-full bg-gray-800 rounded-full p-7 shrink-0"
        onClick={handleSearch}>
        <GeneralSearchIcon width={32} height={32} color="white" />
      </button>
    </div>
  );

  return (
    <LocationFilterProvider
      onNavigateToRecentSearch={() => setOpenedFilter(undefined)}
      variant={variant}>
      <div ref={containerRef} className={cn('relative w-full h-[52px] lg:h-[100px]', className)}>
        <div
          className={cn(
            'flex items-center w-full h-full overflow-hidden bg-white rounded-full lg:hidden gap-2',
            mobileClassName,
          )}>
          <button
            onClick={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
            className="w-full px-6 py-2 overflow-hidden text-left">
            <div className="flex flex-col items-start">
              {!addressSelection ? (
                <Text className="text-sm font-light text-left text-gray-500 truncate">
                  {getSearchBarLabel()}
                </Text>
              ) : (
                <>
                  <Text className="w-full text-sm font-medium text-left truncate">
                    {addressSelection}
                  </Text>
                  <div className="flex flex-row items-start w-full gap-1">
                    <Text className="text-xs truncate text-gray-50">{datesButtonLabel}</Text>
                    {variant !== EHomeSearchWidgetVariant.AutoRentals && (
                      <>
                        <Text className="text-xs truncate text-gray-50">{'•'}</Text>
                        <Text className="text-xs truncate text-gray-50">{guestsButtonLabel}</Text>
                        {shouldDisplayDriverAgeFilter && driverAgeSelection && (
                          <>
                            <Text className="text-xs truncate text-gray-50">{'•'}</Text>
                            <Text className="text-xs truncate text-gray-50">
                              {driverAgeSelection}
                            </Text>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </button>

          <button
            className="flex items-center justify-center w-10 h-10 ml-0 bg-gray-800 rounded-full m-1.5 shrink-0"
            data-testid="mobile-search-button"
            onClick={handleSearch}>
            <GeneralSearchIcon width={21} height={21} color="white" />
          </button>
        </div>

        <form
          onSubmit={async e => {
            e.preventDefault();
            await handleSearch();
          }}
          className={cn('hidden lg:flex items-center bg-white rounded-full', desktopClassName)}>
          <div
            className={cn('flex-[1] pr-2 pl-8 rounded-l-full min-w-0', {
              'z-60': openedFilter === ESearchWidgetFilter.Location,
            })}
            ref={locationFilterButtonRef}>
            <LocationAutocompleteProvider
              variant={variant}
              isOpened={isAboveDesktop && openedFilter === ESearchWidgetFilter.Location}
              onOpen={() => setOpenedFilterYTM(ESearchWidgetFilter.Location)}
              onDataSelection={() => setOpenedFilterYTM(ESearchWidgetFilter.Date)}
              onTrackFilterToggled={trackFilterToggled}>
              <LocationFilterInput label={getSearchBarLabel()} />
              <LocationAutocompleteMenu
                align={ESearchDropdownAlignment.Left}
                searchBarRef={containerRef}
                buttonRef={locationFilterButtonRef}
              />
            </LocationAutocompleteProvider>
          </div>

          <Divider height="100px" variant={EDividerVariant.Vertical} />

          <div
            className={cn('flex-[1]', {
              'z-60': openedFilter === ESearchWidgetFilter.Date,
              'px-2': variant !== EHomeSearchWidgetVariant.AutoRentals,
            })}
            ref={dateFilterButtonRef}>
            <div
              className={cn('flex items-center', {
                'pl-2 pr-1.5 gap-4': variant === EHomeSearchWidgetVariant.AutoRentals,
              })}>
              <DateFilterButton toggleFilter={toggleTargetFilter(ESearchWidgetFilter.Date)} />
            </div>
          </div>
          {variant === EHomeSearchWidgetVariant.AutoRentals && searchButton}

          {(isRentalsVDeliverySearchModeEnabled || variant === EHomeSearchWidgetVariant.Escapes) &&
            variant !== EHomeSearchWidgetVariant.AutoRentals && (
              <>
                <Divider height="100px" variant={EDividerVariant.Vertical} />

                <div
                  className={cn('flex-[1]', {
                    'z-60': openedFilter === ESearchWidgetFilter.Guests,
                  })}
                  ref={guestsFilterButtonRef}>
                  <div className="flex items-center pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
                    <GuestsFilterButton
                      toggleFilter={toggleTargetFilter(ESearchWidgetFilter.Guests)}
                    />
                  </div>
                </div>
                {shouldDisplayDriverAgeFilter ? (
                  <>
                    <Divider height="100px" variant={EDividerVariant.Vertical} />
                    <div
                      className={cn('flex-[1]', {
                        'z-60': openedFilter === ESearchWidgetFilter.DriverAge,
                      })}
                      ref={driverAgeFilterButtonRef}>
                      <div className="flex items-center pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
                        <DriverAgeFilterButton
                          toggleFilter={toggleTargetFilter(ESearchWidgetFilter.DriverAge)}
                        />
                      </div>
                    </div>
                    {searchButton}
                  </>
                ) : (
                  searchButton
                )}
              </>
            )}
          {!isRentalsVDeliverySearchModeEnabled &&
            variant !== EHomeSearchWidgetVariant.Escapes &&
            variant !== EHomeSearchWidgetVariant.AutoRentals && (
              <>
                <Divider height="100px" variant={EDividerVariant.Vertical} />
                <div
                  className={cn('flex-[1]', {
                    'z-60': openedFilter === ESearchWidgetFilter.Guests,
                  })}
                  ref={guestsFilterButtonRef}>
                  <div className="flex items-center pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
                    <GuestsFilterButton
                      toggleFilter={toggleTargetFilter(ESearchWidgetFilter.Guests)}
                    />
                  </div>
                </div>
                {shouldDisplayDriverAgeFilter && (
                  <>
                    <Divider height="100px" variant={EDividerVariant.Vertical} />
                    <div
                      className={cn('flex-[1]', {
                        'z-60': openedFilter === ESearchWidgetFilter.DriverAge,
                      })}
                      ref={driverAgeFilterButtonRef}>
                      <div className="flex items-center pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
                        <DriverAgeFilterButton
                          toggleFilter={toggleTargetFilter(ESearchWidgetFilter.DriverAge)}
                        />
                      </div>
                    </div>
                  </>
                )}
                <Divider height="100px" variant={EDividerVariant.Vertical} />
                <div
                  className={cn('flex-[1] min-w-0', {
                    'z-60': openedFilter === ESearchWidgetFilter.VehicleType,
                  })}
                  ref={vehicleTypeFilterButtonRef}>
                  <div className="flex items-center min-w-0 pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
                    <VehicleTypeFilterButton
                      toggleFilter={toggleTargetFilter(ESearchWidgetFilter.VehicleType)}
                      className="min-w-0"
                    />
                  </div>
                </div>
                {searchButton}
              </>
            )}
        </form>

        <TripInformationHomeContent
          variant={variant}
          isOpened={!isAboveDesktop && openedFilter === ESearchWidgetFilter.TripInformation}
          onClose={() => setOpenedFilterYTM(undefined)}
          onSelectFilter={setOpenedFilterYTM}
          onSearch={handleSearch}
          dateButtonLabel={datesButtonLabel}
          datesSelection={datesSelection}
          driverAgeButtonLabel={driverAgeButtonLabel}
          driverAgeSelection={driverAgeSelection}
          guestsButtonLabel={guestsButtonLabel}
          guestsSelection={guestsSelection}
          vehicleTypeButtonLabel={vehicleTypeButtonLabel}
          vehicleTypeSelection={vehicleTypeSelection}
        />

        <LocationFilterContent
          variant={variant}
          onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          isOpened={!isAboveDesktop && openedFilter === ESearchWidgetFilter.Location}
          onDataSelection={() => {
            if (isAboveDesktop) {
              setOpenedFilterYTM(ESearchWidgetFilter.Date);
            } else {
              setOpenedFilterYTM(ESearchWidgetFilter.TripInformation);
            }
          }}
          buttonRef={locationFilterButtonRef}
          searchBarRef={containerRef}
          align={ESearchDropdownAlignment.Left}
        />

        <DateFilterContent
          onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          onDataSelection={() => {
            if (isAboveDesktop) {
              if (variant === EHomeSearchWidgetVariant.AutoRentals) {
                setOpenedFilterYTM(undefined);
              } else {
                setOpenedFilterYTM(ESearchWidgetFilter.Guests);
              }
            }
          }}
          onDone={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          isOpened={openedFilter === ESearchWidgetFilter.Date}
          onTrackFilterToggled={trackFilterToggled}
          allowSameDay={variant === EHomeSearchWidgetVariant.AutoRentals}
          buttonRef={dateFilterButtonRef}
          searchBarRef={containerRef}
          align={ESearchDropdownAlignment.Center}
        />

        <GuestsFilterContent
          onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          onDone={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          isOpened={openedFilter === ESearchWidgetFilter.Guests}
          onTrackFilterToggled={trackFilterToggled}
          buttonRef={guestsFilterButtonRef}
          searchBarRef={containerRef}
          align={ESearchDropdownAlignment.Left}
        />

        {shouldDisplayDriverAgeFilter && (
          <DriverAgeFilterContent
            onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
            onDone={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
            onDataSelection={() => {
              if (isAboveDesktop) {
                if (isRentalsVDeliverySearchModeEnabled) {
                  setOpenedFilterYTM(undefined);
                } else {
                  setOpenedFilterYTM(ESearchWidgetFilter.VehicleType);
                }
              }
            }}
            isOpened={openedFilter === ESearchWidgetFilter.DriverAge}
            onTrackFilterToggled={trackFilterToggled}
            buttonRef={driverAgeFilterButtonRef}
            searchBarRef={containerRef}
            align={ESearchDropdownAlignment.Center}
          />
        )}

        <VehicleTypeFilterContent
          onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          onDone={() => setOpenedFilterYTM(ESearchWidgetFilter.TripInformation)}
          isOpened={openedFilter === ESearchWidgetFilter.VehicleType}
          onTrackFilterToggled={trackFilterToggled}
          buttonRef={vehicleTypeFilterButtonRef}
          searchBarRef={containerRef}
          align={ESearchDropdownAlignment.Right}
          vehicleTypeOptions={vehicleTypeOptions}
        />
      </div>
    </LocationFilterProvider>
  );
};

const useAddressSelection = () => {
  return useSelector(getHeaderSearchAddressFilter);
};

const useDatesSelection = () => {
  const dates = useSelector(getHeaderSearchDatesFilter);
  return formatDateRangeWithFlexibleDays(dates?.from, dates?.to, dates?.flexible_days);
};

const useTotalGuestsFromSelection = () => {
  const guests = useSelector(getHeaderSearchGuestsFilter);

  return (guests?.adults || 0) + (guests?.children || 0) + (guests?.infants || 0) || 0;
};

const useGuestsSelection = () => {
  const intl = useIntl();

  const guests = useSelector(getHeaderSearchGuestsFilter);
  const totalGuests = (guests?.adults || 0) + (guests?.children || 0);

  if (totalGuests) {
    return intl.formatMessage(
      {
        defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
        id: '5Y37yc',
        description: 'Home Search Widget > Guests Filter Selected Value',
      },
      {
        totalGuests,
      },
    );
  }
};

const useVehicleTypeSelection = () => {
  const vehicleType = useSelector(getHeaderSearchVehicleTypeFilter);

  return vehicleType;
};

const useDriverAgeSelection = () => {
  const driverAge = useSelector(getHeaderSearchDriverAgeFilter);

  if (driverAge === 21) {
    return '21-24';
  }
  if (driverAge === 25) {
    return '25+';
  }
  return null;
};
